/*
 This file is part of GNU Taler
 (C) 2022 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  composeTranslationCatalogues,
  i18n,
  setupI18n,
} from "@gnu-taler/taler-util";
import { ComponentChildren, createContext, h, VNode } from "preact";
import { useContext, useEffect, useMemo, useRef } from "preact/hooks";
import { strings as webUtilStrings, StringsType } from "../i18n/strings.js";
import { useLang } from "../hooks/index.js";
import { Locale } from "date-fns";
import esLocale from "date-fns/locale/es/index.js";
import enLocale from "date-fns/locale/en-GB/index.js";
import frLocale from "date-fns/locale/fr/index.js";
import deLocale from "date-fns/locale/de/index.js";
import itLocale from "date-fns/locale/it/index.js";

export type InternationalizationAPI = typeof i18n;

interface Type {
  lang: string;
  supportedLang: Record<string, string>;
  changeLanguage: (l: string) => void;
  i18n: InternationalizationAPI;
  dateLocale: Locale;
  completeness: Record<string, number>;
}

const SUPPORTED_LANGS = {
  es: "Español [es]",
  en: "English [en]",
  fr: "Français [fr]",
  de: "Deutsch [de]",
  "de-CH": "Deutsch (Schweiz) [de-CH]",
  // sv: "Svenska [sv]",
  it: "Italiano [it]",
};

const initial: Type = {
  lang: "en",
  supportedLang: SUPPORTED_LANGS,
  changeLanguage: () => {
    // do not change anything
  },
  i18n,
  dateLocale: enLocale,
  completeness: {
    de: 0,
    en: 0,
    es: 0,
    fr: 0,
    "de-CH": 0,
    it: 0,
  },
};
const Context = createContext<Type>(initial);

function canonicalLanguageCode(language: string): string {
  const normalized = language.replaceAll("_", "-");
  if (normalized.toLowerCase() === "de-ch") return "de-CH";
  return normalized.toLowerCase();
}

interface Props {
  initial?: string;
  children: ComponentChildren;
  /** ONLY USER FOR TESTING */
  forceLang__testing?: string;
  source: Record<string, StringsType>;
}

// Outmost UI wrapper.
export const TranslationProvider = ({
  initial,
  children,
  forceLang__testing: forceLang,
  source,
}: Props): VNode => {
  const mergedSource = useMemo(
    () => composeTranslationCatalogues(webUtilStrings, source),
    [source],
  );
  const availableLanguages = new Set([
    "en",
    ...Object.keys(source).map(canonicalLanguageCode),
  ]);
  const supportedLang = Object.fromEntries(
    Object.entries(SUPPORTED_LANGS).filter(([lang]) =>
      availableLanguages.has(lang),
    ),
  );
  const completeness = Object.keys(supportedLang).reduce(
    (map, lang) => {
      if (
        lang !== "en" &&
        mergedSource[lang] &&
        mergedSource[lang].completeness
      ) {
        map[lang] = mergedSource[lang].completeness;
      }
      return map;
    },
    { en: 100 } as Record<string, number>,
  );

  const { value: lang, update: changeLanguage } = useLang(
    initial,
    completeness,
  );
  const changeLanguageRef = useRef(changeLanguage);
  changeLanguageRef.current = changeLanguage;

  useEffect(() => {
    if (forceLang) {
      changeLanguageRef.current(forceLang);
    }
  }, [forceLang]);
  const effectiveLang = canonicalLanguageCode(forceLang ?? lang);
  setupI18n(effectiveLang, mergedSource);

  const dateLocale =
    effectiveLang === "es"
      ? esLocale
      : effectiveLang === "fr"
        ? frLocale
        : effectiveLang === "de" || effectiveLang === "de-CH"
          ? deLocale
          : effectiveLang === "it"
            ? itLocale
            : enLocale;

  return h(Context.Provider, {
    value: {
      lang: effectiveLang,
      changeLanguage,
      supportedLang,
      i18n,
      dateLocale,
      completeness,
    },
    children,
  });
};

export const useTranslationContext = (): Type => useContext(Context);
